/**
 * ArticleDetail - View a single advice article with markdown content
 * 
 * CONTENT GATING: Shows ~250 word preview to non-subscribers,
 * with full content only visible to users with active paid subscription.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icons } from '../../lib/Icons';

const SITE_URL = 'https://sen-education.co.uk';

// Use PNG default; LinkedIn/Facebook do not support SVG for og:image
function getArticleOgImage(article) {
  if (article?.coverImage) {
    const url = article.coverImage.trim();
    return url.startsWith('http') ? url : `${SITE_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  }
  return `${SITE_URL}/brand/Full%20Logo%20cropped.png`;
}

// Helper function to transform text content, replacing emojis with icons
const transformTextWithIcons = (children) => {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      // Split on emoji patterns and map to components
      const parts = child.split(/(❌|✅|⚠️)/);
      if (parts.length === 1) return child;

      return parts.map((part, i) => {
        if (part === '❌') {
          return <Icons.XCircle key={i} className="w-5 h-5 inline-block text-red-500 mr-1 -mt-0.5" />;
        }
        if (part === '✅') {
          return <Icons.CheckCircle key={i} className="w-5 h-5 inline-block text-green-500 mr-1 -mt-0.5" />;
        }
        if (part === '⚠️') {
          return <Icons.ExclamationTriangle key={i} className="w-5 h-5 inline-block text-amber-500 mr-1 -mt-0.5" />;
        }
        return part;
      });
    }
    // If child is a React element with children, recursively transform
    if (React.isValidElement(child) && child.props.children) {
      return React.cloneElement(child, {
        ...child.props,
        children: transformTextWithIcons(child.props.children)
      });
    }
    return child;
  });
};

// Custom components for ReactMarkdown to replace emojis with icons
const markdownComponents = {
  p: ({ children, node, ...props }) => (
    <p {...props}>{transformTextWithIcons(children)}</p>
  ),
  li: ({ children, node, ...props }) => (
    <li {...props}>{transformTextWithIcons(children)}</li>
  ),
  blockquote: ({ children, node, ...props }) => (
    <blockquote {...props}>{transformTextWithIcons(children)}</blockquote>
  ),
  strong: ({ children, node, ...props }) => (
    <strong {...props}>{transformTextWithIcons(children)}</strong>
  ),
};

// Category configuration (same as ArticlesPage)
const CATEGORIES = {
  strategies: {
    label: 'Classroom Strategies',
    color: 'from-blue-500 to-cyan-500',
    icon: Icons.Puzzle
  },
  statutory: {
    label: 'Statutory Processes',
    color: 'from-purple-500 to-violet-500',
    icon: Icons.ClipboardDocumentList
  },
  'parent-communication': {
    label: 'Parent Communication',
    color: 'from-green-500 to-emerald-500',
    icon: Icons.ChatBubble
  },
  'specific-needs': {
    label: 'Specific Needs',
    color: 'from-rose-500 to-pink-500',
    icon: Icons.Heart
  },
  wellbeing: {
    label: 'Wellbeing',
    color: 'from-amber-500 to-orange-500',
    icon: Icons.Star
  },
};

// Helper function to get preview content (first ~250 words)
// This version preserves paragraph structure to avoid breaking markdown
const getPreviewContent = (content, wordLimit = 250) => {
  if (!content) return { content: '', isTruncated: false };

  // Count total words
  const totalWords = content.split(/\s+/).length;
  if (totalWords <= wordLimit) {
    return { content, isTruncated: false };
  }

  // Split by double newlines (paragraphs) to preserve markdown structure
  const paragraphs = content.split(/\n\n+/);

  let wordCount = 0;
  let previewParagraphs = [];

  for (const paragraph of paragraphs) {
    // Skip markdown headings after the first couple of paragraphs
    // to avoid showing "###" in preview
    if (previewParagraphs.length > 2 && paragraph.trim().startsWith('#')) {
      break;
    }

    // Skip horizontal rules
    if (paragraph.trim() === '---') {
      break;
    }

    const paragraphWords = paragraph.split(/\s+/).length;

    if (wordCount + paragraphWords > wordLimit && previewParagraphs.length > 0) {
      // We have enough content, stop here to maintain clean paragraph breaks
      break;
    }

    previewParagraphs.push(paragraph);
    wordCount += paragraphWords;

    // Stop after reaching approximately the word limit
    if (wordCount >= wordLimit * 0.8) {
      break;
    }
  }

  return {
    content: previewParagraphs.join('\n\n'),
    isTruncated: true
  };
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [linkedResources, setLinkedResources] = useState({ activities: [], articles: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth and subscription state
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Check auth and subscription status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const functions = getFunctions(undefined, 'europe-west3');
          const checkSub = httpsCallable(functions, 'checkSoloSubscriptionFunc');
          const result = await checkSub();
          setSubscriptionStatus(result.data);
        } catch (e) {
          console.error('Subscription check failed:', e);
          setSubscriptionStatus({ hasActiveSubscription: false, status: 'none' });
        }
      } else {
        setSubscriptionStatus(null);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions(undefined, 'europe-west3');
      const getArticle = httpsCallable(functions, 'getSensiArticleFunc');

      const result = await getArticle({ slug });

      if (result.data?.article) {
        setArticle(result.data.article);
        setLinkedResources(result.data.linkedResources || { activities: [], articles: [] });
      } else {
        setError('Article not found');
      }
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has full access (active subscription)
  const hasFullAccess = subscriptionStatus?.hasActiveSubscription === true;

  // Get the return URL for signup/checkout redirects
  const returnUrl = encodeURIComponent(location.pathname);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-sm border border-slate-200 flex items-center justify-center">
            <Icons.XMark className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">{error || 'Article not found'}</h2>
          <Link
            to="/solo/articles"
            className="text-violet-600 hover:text-violet-500"
          >
            Browse all articles
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = CATEGORIES[article.category] || CATEGORIES.strategies;
  const CategoryIcon = categoryInfo.icon;

  // Get preview or full content based on subscription
  const { content: displayContent, isTruncated } = hasFullAccess
    ? { content: article.content, isTruncated: false }
    : getPreviewContent(article.content);

  const canonicalUrl = article ? `${SITE_URL}/solo/articles/${article.slug}` : SITE_URL;
  const ogImage = article ? getArticleOgImage(article) : `${SITE_URL}/brand/Full%20Logo%20cropped.png`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {article && (
        <Helmet>
          <title>{article.title} | SEN-Ed Advice Hub</title>
          <meta name="description" content={article.summary?.slice(0, 160)} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.summary} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="SEN-Ed" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={article.summary} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Icons.ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <Link
                to="/solo/articles"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                Advice Hub
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 bg-gradient-to-r ${categoryInfo.color} rounded-full text-xs text-white font-medium`}>
                {categoryInfo.label}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Article Hero */}
      <section className={`relative overflow-hidden py-12 bg-gradient-to-br ${categoryInfo.color}`}>
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-2 mb-4">
            <CategoryIcon className="w-6 h-6 text-white/80" />
            <span className="text-white/80 text-sm">{categoryInfo.label}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {article.title}
          </h1>
          <p className="text-lg text-white/90 mb-6">
            {article.summary}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            {article.readingTimeMinutes && (
              <span className="flex items-center gap-1">
                <Icons.Clock className="w-4 h-4" />
                {article.readingTimeMinutes} min read
              </span>
            )}
            {article.author && (
              <span className="flex items-center gap-1">
                <Icons.User className="w-4 h-4" />
                {article.author}
              </span>
            )}
            {article.publishedAt && (
              <span>
                {new Date(article.publishedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Article Content */}
          <article className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-slate-200 relative">
            <div className={`prose prose-lg max-w-none
              prose-headings:text-slate-900 prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5 prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-3
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-3
              prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-5
              prose-a:text-violet-600 prose-a:no-underline hover:prose-a:text-violet-500 hover:prose-a:underline
              prose-strong:text-slate-900 prose-strong:font-semibold
              prose-ul:text-slate-700 prose-ul:my-5 prose-ul:space-y-2
              prose-ol:text-slate-700 prose-ol:my-5 prose-ol:space-y-2
              prose-li:my-2 prose-li:leading-relaxed
              prose-blockquote:border-l-4 prose-blockquote:border-l-violet-500 prose-blockquote:bg-violet-50 prose-blockquote:py-4 prose-blockquote:px-5 prose-blockquote:my-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-slate-700
              prose-code:text-violet-600 prose-code:bg-violet-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-slate-50 prose-pre:border prose-pre:border-slate-200 prose-pre:my-6
              prose-table:text-slate-700 prose-table:my-6
              prose-th:bg-slate-100 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:text-slate-900 prose-th:border-b prose-th:border-slate-200
              prose-td:px-4 prose-td:py-3 prose-td:border-b prose-td:border-slate-100
              prose-hr:border-slate-200 prose-hr:my-8
              prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
            `}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {displayContent}
              </ReactMarkdown>
            </div>

            {/* Content Gate Overlay - shown when content is truncated */}
            {isTruncated && (
              <>
                {/* Fade gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none"
                  style={{ bottom: '180px' }} />

                {/* Paywall CTA Card */}
                <div className="relative mt-8 p-8 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200 shadow-lg">
                  <div className="text-center max-w-md mx-auto">
                    {/* Lock Icon */}
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Icons.LockClosed className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Continue reading with SEN-Ed
                    </h3>

                    <p className="text-slate-600 mb-6">
                      Unlock this article and get access to 500+ SEND resources, evidence-based activities, and expert guidance.
                    </p>

                    {/* CTA based on auth state */}
                    {!user ? (
                      <>
                        {/* Not logged in - show signup CTA */}
                        <Link
                          to={`/signup/solo?returnUrl=${returnUrl}`}
                          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                        >
                          <Icons.Sparkles className="w-5 h-5" />
                          Start Free Trial
                        </Link>
                        <p className="mt-4 text-sm text-slate-500">
                          Already have an account?{' '}
                          <Link
                            to={`/login?returnUrl=${returnUrl}`}
                            className="text-violet-600 hover:text-violet-500 font-medium"
                          >
                            Sign in
                          </Link>
                        </p>
                      </>
                    ) : (
                      <>
                        {/* Logged in but no subscription - show subscribe CTA */}
                        <Link
                          to={`/solo/checkout?returnUrl=${returnUrl}`}
                          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                        >
                          <Icons.Star className="w-5 h-5" />
                          Subscribe to Continue Reading
                        </Link>
                        <p className="mt-4 text-sm text-slate-500">
                          7-day free trial • Cancel anytime
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Sources - only show for full access */}
            {hasFullAccess && article.sources?.length > 0 && (
              <div className="mt-12 pt-8 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Icons.Link className="w-5 h-5 text-violet-500" />
                  Sources & References
                </h3>
                <ul className="space-y-2">
                  {article.sources.map((source, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      {source.url ? (
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-violet-600 hover:text-violet-500 hover:underline"
                        >
                          {source.name}
                          <Icons.ArrowTopRightOnSquare className="w-3 h-3 inline ml-1" />
                        </a>
                      ) : (
                        <span className="text-slate-600">{source.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags - only show for full access */}
            {hasFullAccess && article.tags?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar - Related Resources */}
          <aside className="lg:col-span-1 space-y-6">
            {/* Related Activities */}
            {linkedResources.activities?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Icons.Puzzle className="w-5 h-5 text-blue-500" />
                  Related Activities
                </h3>
                <div className="space-y-3">
                  {linkedResources.activities.map(activity => (
                    <Link
                      key={activity.slug}
                      to={`/solo/activity/${activity.slug}`}
                      className="block p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                    >
                      <h4 className="font-medium text-slate-900 group-hover:text-blue-600 transition-colors text-sm">
                        {activity.title}
                      </h4>
                      {activity.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {activity.description}
                        </p>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {linkedResources.articles?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Icons.BookOpen className="w-5 h-5 text-violet-500" />
                  Related Articles
                </h3>
                <div className="space-y-3">
                  {linkedResources.articles.map(relatedArticle => (
                    <Link
                      key={relatedArticle.slug}
                      to={`/solo/articles/${relatedArticle.slug}`}
                      className="block p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                    >
                      <h4 className="font-medium text-slate-900 group-hover:text-violet-600 transition-colors text-sm">
                        {relatedArticle.title}
                      </h4>
                      {relatedArticle.summary && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {relatedArticle.summary}
                        </p>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Ask Sensi CTA */}
            <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Icons.Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Have questions?</h3>
                  <p className="text-xs text-white/70">Ask Sensi for help</p>
                </div>
              </div>
              <p className="text-sm text-white/90 mb-4">
                Need more specific advice? Chat with Sensi, your AI SEND assistant.
              </p>
              <Link
                to="/solo/sensi"
                className="block w-full py-2 px-4 bg-white text-violet-600 text-center font-medium rounded-xl hover:bg-violet-50 transition-all text-sm"
              >
                Ask Sensi
              </Link>
            </div>

            {/* Back to Articles */}
            <Link
              to="/solo/articles"
              className="flex items-center justify-center gap-2 py-3 px-4 bg-white text-slate-600 rounded-xl hover:bg-slate-50 hover:text-slate-900 transition-colors text-sm shadow-sm border border-slate-200"
            >
              <Icons.ChevronLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
